/**
 * CorporatePanel — Corporate Accounts (Phase 1) admin home.
 *
 * Reporting summary + company list + create-company, and drills into a single
 * company (CompanyDetail) for profile / customers / ledger / statement. All
 * read-only over the ride lifecycle.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { AiAssist } from "../../components/AiAssist";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../../components/ui/dialog";
import { Building2, Plus, RefreshCw, TrendingUp, Wallet, Send } from "lucide-react";
import { CompanyDetail } from "./CompanyDetail";
import { useFinancialPrivacy } from "../../lib/financialPrivacy";

const gbp = (n) => {
  const v = Number(n ?? 0);
  return v < 0 ? `-£${Math.abs(v).toFixed(2)}` : `£${v.toFixed(2)}`;
};
const balText = (v) => (Number(v) < 0 ? `Credit £${Math.abs(Number(v)).toFixed(2)}` : gbp(v));
const balColor = (v) => (Number(v) > 0 ? "#EF4444" : Number(v) < 0 ? "#002FA7" : "#10B981");

const SummaryCard = ({ label, value, icon, accent = "#0A0A0A", testid }) => (
  <div className="bg-white border border-border p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="label-mini text-muted-foreground">{label}</div>
      <span style={{ color: accent }}>{icon}</span>
    </div>
    <div className="font-heading text-3xl font-black mt-1" style={{ color: accent }} data-testid={`${testid}-value`}>{value}</div>
  </div>
);

export function CorporatePanel({ passengers, onRefreshPassengers }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [invoicing, setInvoicing] = useState(false);
  const [form, setForm] = useState({ name: "", contact_name: "", billing_email: "", phone: "", payment_terms_days: 30 });
  const { mask } = useFinancialPrivacy();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/corporate/report");
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load corporate report");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCompany = async () => {
    if (!form.name.trim()) { toast.error("Company name is required"); return; }
    try {
      const { data } = await api.post("/admin/companies", {
        name: form.name.trim(),
        contact_name: form.contact_name || null,
        billing_email: form.billing_email || null,
        phone: form.phone || null,
        payment_terms_days: Number(form.payment_terms_days) || 30,
      });
      toast.success("Company created");
      setOpen(false);
      setForm({ name: "", contact_name: "", billing_email: "", phone: "", payment_terms_days: 30 });
      await load();
      setSelected(data.id);
    } catch (e) { toast.error(formatApiError(e) || "Create failed"); }
  };

  const afterChange = async () => { await load(); onRefreshPassengers?.(); };

  // Manual "Generate & email now" — same code path as the monthly scheduler:
  // emails every active company its previous-month statement (idempotent).
  const runAutoInvoice = async () => {
    if (!window.confirm("Generate and email last month's statement to every active company with a billing email? (Companies already sent this month are skipped.)")) return;
    setInvoicing(true);
    try {
      const { data } = await api.post("/admin/corporate/auto-invoice/run", {});
      toast.success(`Auto-invoicing for ${data.month}: ${data.sent} sent, ${data.skipped} skipped${data.failed ? `, ${data.failed} failed` : ""}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Auto-invoicing failed");
    } finally {
      setInvoicing(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground" data-testid="corporate-loading"><RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading corporate accounts…</div>;
  }

  if (selected) {
    return (
      <div className="p-4 md:p-6">
        <CompanyDetail companyId={selected} passengers={passengers} onBack={() => { setSelected(null); load(); }} onChanged={afterChange} />
      </div>
    );
  }

  const allCompanies = report?.companies || [];
  const companies = filter === "all" ? allCompanies : allCompanies.filter((c) => c.status === filter);

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="corporate-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Building2 className="w-6 h-6 text-[#002FA7]" />
          <div>
            <h2 className="font-heading text-2xl font-black tracking-tight">Corporate Accounts</h2>
            <div className="text-xs text-muted-foreground">Company billing · account ride attribution · ledger & monthly statements</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runAutoInvoice} disabled={invoicing} variant="outline" data-testid="corporate-auto-invoice" title="Email last month's statement to all active companies (same as the monthly scheduler)">
            <Send className="w-4 h-4 mr-1" /> {invoicing ? "Sending…" : "Generate & email now"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0A0A0A] text-white" data-testid="corporate-new-company"><Plus className="w-4 h-4 mr-1" /> New company</Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New corporate account</DialogTitle>
              <DialogDescription>Create a company to attribute account rides, track its ledger and issue monthly statements.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <AiAssist
                formTitle="New corporate account"
                testId="company-ai-assist"
                values={form}
                onFill={(v) => setForm((prev) => ({ ...prev, ...v }))}
                schema={[
                  { name: "name", label: "Company name", type: "text", required: true },
                  { name: "contact_name", label: "Contact name", type: "text" },
                  { name: "billing_email", label: "Billing email", type: "email" },
                  { name: "phone", label: "Phone", type: "phone" },
                  { name: "payment_terms_days", label: "Payment terms in days", type: "integer", description: "e.g. 14, 30, 60" },
                ]}
              />
              <Input placeholder="Company name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-company-name" />
              <Input placeholder="Contact name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="new-company-contact" />
              <Input placeholder="Billing email" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} data-testid="new-company-email" />
              <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-company-phone" />
              <label className="block"><span className="label-mini text-muted-foreground">PAYMENT TERMS (DAYS)</span>
                <Input type="number" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} className="mt-1" data-testid="new-company-terms" />
              </label>
              <Button onClick={createCompany} className="w-full bg-[#0A0A0A] text-white" data-testid="new-company-submit">Create company</Button>
            </div>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Reporting summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="corporate-summary">
        <SummaryCard label="ACTIVE ACCOUNTS" value={report.active_count} icon={<Building2 className="w-4 h-4" />} testid="corporate-companies" />
        <SummaryCard label="MONTHLY CORPORATE REVENUE" value={mask(gbp(report.mtd_charges))} accent="#002FA7" icon={<TrendingUp className="w-4 h-4" />} testid="corporate-mtd" />
        <SummaryCard label="TOTAL RECEIVABLE" value={mask(balText(report.total_receivable))} accent={balColor(report.total_receivable)} icon={<Wallet className="w-4 h-4" />} testid="corporate-receivable" />
        <SummaryCard label="WITH BALANCE" value={report.companies_with_balance} icon={<Wallet className="w-4 h-4" />} testid="corporate-withbalance" />
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2" data-testid="corporate-filter">
        {["all", "active", "suspended"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs font-bold uppercase px-3 py-1.5 border ${filter === f ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border text-muted-foreground hover:bg-secondary"}`}
            data-testid={`corporate-filter-${f}`}
          >
            {f}{f === "all" ? ` (${report.company_count})` : f === "active" ? ` (${report.active_count})` : ` (${report.suspended_count})`}
          </button>
        ))}
      </div>

      {/* Company list */}
      <div className="bg-white border border-border overflow-x-auto" data-testid="corporate-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-secondary">
            <th className="px-3 py-2 text-left label-mini text-muted-foreground">Company</th>
            <th className="px-3 py-2 text-right label-mini text-muted-foreground">Customers</th>
            <th className="px-3 py-2 text-right label-mini text-muted-foreground">MTD rides</th>
            <th className="px-3 py-2 text-right label-mini text-muted-foreground">MTD charges</th>
            <th className="px-3 py-2 text-right label-mini text-muted-foreground">Balance</th>
          </tr></thead>
          <tbody>
            {companies.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground italic" data-testid="corporate-empty">No corporate accounts yet. Create your first company to start attributing account rides.</td></tr>
            ) : companies.map((c) => (
              <tr key={c.id} onClick={() => setSelected(c.id)} className="border-b border-border last:border-0 hover:bg-secondary/50 cursor-pointer" data-testid={`corporate-row-${c.id}`}>
                <td className="px-3 py-2 font-bold">
                  {c.name}
                  {c.status === "suspended" && <span className="ml-2 text-[10px] uppercase font-bold text-[#991B1B] bg-[#FEE2E2] px-1.5 py-0.5" data-testid={`corporate-suspended-${c.id}`}>suspended</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono-tab">{c.customers}</td>
                <td className="px-3 py-2 text-right font-mono-tab">{c.mtd.rides}</td>
                <td className="px-3 py-2 text-right font-mono-tab">{mask(gbp(c.mtd.charges))}</td>
                <td className="px-3 py-2 text-right font-mono-tab font-bold" style={{ color: balColor(c.balance) }}>{mask(balText(c.balance))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CorporatePanel;
